import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { Shield, Eye, Lock, AlertTriangle, Server, Code, Cloud, Database, CheckCircle, ShieldCheck } from "lucide-react";
import { NetworkSecurityIcon } from "../../components/icons/NetworkSecurityIcon";
import { CloudSecurityIcon } from "../../components/icons/CloudSecurityIcon";
import { EndpointSecurityIcon } from "../../components/icons/EndpointSecurityIcon";
import { DataProtectionIcon } from "../../components/icons/DataProtectionIcon";
import { AppSecurityIcon } from "../../components/icons/AppSecurityIcon";
import { RelatedServices } from "@/components/RelatedServices";
import WebDesignLocations from '@/components/WebDesignLocations';

const SecurityServiceCard = ({ title, description, icon: Icon, color }: { title: string; description: string; icon: React.FC<React.SVGProps<SVGSVGElement>>; color: string }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ y: -5 }}
    className="bg-white rounded-xl p-8 hover:shadow-lg hover:shadow-blue-100 transition-all border border-gray-200"
  >
    <div className={`w-16 h-16 rounded-lg bg-${color}-100 flex items-center justify-center mb-6 text-${color}-600`}>
      <Icon className="h-8 w-8" />
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </motion.div>
);

export default function CyberSecurity() {
  const { openDrawer } = useQuoteDrawer();

  const services = [
    {
      title: "Security Assessment",
      description: "Comprehensive security audits to identify vulnerabilities and security gaps in your systems.",
      icon: Eye,
      color: "blue"
    },
    {
      title: "Threat Protection",
      description: "Advanced threat detection and prevention systems to protect against cyber attacks.",
      icon: Shield,
      color: "red"
    },
    {
      title: "Data Encryption",
      description: "End-to-end encryption solutions to protect sensitive data in transit and at rest.",
      icon: Lock,
      color: "purple"
    },
    {
      title: "Incident Response",
      description: "24/7 security monitoring and rapid incident response to minimize security breaches.",
      icon: AlertTriangle,
      color: "orange"
    }
  ];

  const securityAreas = [
    { 
      title: "Network Security", 
      icon: NetworkSecurityIcon,
      description: "Comprehensive protection for your network infrastructure",
      features: ["Firewall configuration", "Intrusion detection", "VPN & secure access", "Network segmentation"],
      color: "bg-blue-100"
    },
    { 
      title: "Cloud Security", 
      icon: CloudSecurityIcon,
      description: "End-to-end security for cloud environments",
      features: ["Cloud security posture", "Identity management", "Data encryption", "Compliance monitoring"],
      color: "bg-purple-100"
    },
    { 
      title: "Endpoint Security", 
      icon: EndpointSecurityIcon,
      description: "Advanced protection for all devices",
      features: ["Next-gen antivirus", "Device control", "Vulnerability management", "Patch management"],
      color: "bg-green-100"
    },
    { 
      title: "Data Protection", 
      icon: DataProtectionIcon,
      description: "Safeguard your sensitive information",
      features: ["Data loss prevention", "Encryption keys", "Data classification", "Backup & recovery"],
      color: "bg-yellow-100"
    },
    {
      title: "Application Security",
      icon: AppSecurityIcon,
      description: "Secure your applications throughout their lifecycle",
      features: ["Security testing", "API security", "Web application firewall", "Secure SDLC"],
      color: "bg-red-100"
    }
  ];

  return (
    <>
      <SEO 
        title="Cyber Security Services UAE | Kecly"
        description="Enterprise-grade cybersecurity services in UAE. Protect your business with our penetration testing, vulnerability assessments, and security audits."
        keywords="cybersecurity uae, penetration testing dubai, security audits sharjah"
      />

      <main>


        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 via-purple-50 to-blue-50 py-24 px-4 sm:px-6 lg:px-8 text-gray-900 overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mx-auto text-center"
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 mb-6">
                <Shield className="h-5 w-5 mr-2 text-white" />
                <span className="text-sm font-medium text-white">Cyber Security</span>
              </div>
              <h1 className="font-orbitron font-bold text-5xl md:text-6xl text-gray-900 mb-6">
                Advanced <span className="gradient-text">Cyber Protection</span>
              </h1>
              <p className="text-xl text-gray-600 mb-10">
              Comprehensive security solutions to safeguard your digital assets
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                  onClick={openDrawer}
                >
                  Get Security Audit
                </Button>
                <Button size="lg" variant="outline" className="text-gray-900 border-gray-900 hover:bg-gray-100">
                  Learn More
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Security Services</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">Comprehensive solutions to protect your digital assets</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <SecurityServiceCard 
                  key={index}
                  title={service.title}
                  description={service.description}
                  icon={service.icon}
                  color={service.color}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Security Areas Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Security Focus Areas</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">Specialized protection for all aspects of your infrastructure</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
              {securityAreas.map((area, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-sm transition-all border border-gray-200"
                >
                  <div className={`w-16 h-16 ${area.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <area.icon className="w-8 h-8 text-gray-900" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{area.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{area.description}</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    {area.features.map((feature, i) => (
                      <li key={i} className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <RelatedServices 
              currentService="Cyber Security" 
              services={[
                {
                  title: "Cloud Services",
                  description: "Scalable, secure, and high-performance cloud solutions tailored to your business needs.",
                  icon: <Cloud className="h-8 w-8" />
                },
                {
                  title: "Data Analytics",
                  description: "Protect and secure your business data with advanced analytics solutions.",
                  icon: <Database className="h-8 w-8" />
                },
                {
                  title: "Software Development",
                  description: "Secure custom software solutions built with security best practices.",
                  icon: <Code className="h-8 w-8" />
                }
              ]} 
            />
          </div>
        </section>

        {/* Custom Solutions Section */}
        <section className="py-16 bg-blue-50 rounded-xl border border-gray-200">
          <div className="container mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="md:w-2/3">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Need Custom Security Solutions?</h3>
                <p className="text-gray-600 mb-6">
                  Our team of security experts can design a custom security strategy tailored to your specific business needs and compliance requirements.
                </p>
                <Button 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                  onClick={openDrawer}
                >
                  Request a Consultation
                </Button>
              </div>
              <div className="hidden md:block">
                <div className="relative w-40 h-40">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-2xl -rotate-6"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-2xl rotate-6"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <ShieldCheck className="w-16 h-16 text-blue-400" strokeWidth={1.5} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <WebDesignLocations />

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
          <div className="container mx-auto px-6 text-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-orbitron font-bold text-4xl text-white mb-6">Ready to Secure Your Business?</h2>
              <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto">
                Get started with our comprehensive cyber security solutions today
              </p>
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-gray-100 hover:text-blue-700 font-medium"
                onClick={openDrawer}
              >
                Get Protected Now
              </Button>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
